import {
  allotmentActive,
  allotmentAmount,
  DEFAULT_PAYROLL_CURRENCY,
  daysInPayrollMonth,
  earnedBasic,
  overtimeAmountOf,
  PAYROLL_ALLOTMENT_BASES,
  PAYROLL_COMMON_CURRENCIES,
  PAYROLL_DEDUCTION_DESCRIBED,
  PAYROLL_DEDUCTION_TYPES,
  PAYROLL_ENTRY_KINDS,
  PAYROLL_EXTRA_TYPES,
  PAYROLL_PAYMENT_METHODS,
  PAYROLL_PAYMENT_TYPES,
  type PayrollAllotmentBasis,
  type PayrollEntryKind,
  type PayrollExtraType,
  type PayrollLineResult,
  type PayrollPaymentMethod,
  type PayrollPaymentType,
  payrollDeductionItems,
  payrollExtraItems,
  payrollExtraTotal,
} from '@felix/domain';
import {
  ActionIcon,
  Anchor,
  Button,
  FileButton,
  NumberInput,
  Select,
  Textarea,
  TextInput,
} from '@mantine/core';
import { IconPlus, IconUpload, IconX } from '@tabler/icons-react';
import { createContext, type ReactNode, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DayInput } from '../../inputs/DayInput.js';
import { StatusBadge } from '../../StatusBadge.js';
import { useUnsavedChanges } from '../../shell-context.js';
import { errorCode } from '../personnel/source.js';
import { AttachmentCard } from '../pms/AttachmentCard.js';
import classes from './Payroll.module.css';
import { usePayrollError } from './PayrollDialogs.js';
import type { PayrollData, PayrollDocument, PayrollKind, PayrollPayment } from './types.js';
import {
  deductionLabel,
  deductionTypeLabel,
  extraLabel,
  extraTypeLabel,
  formatMoney,
  fromMinor,
  monthLabel,
  toMinor,
} from './view.js';

/** Dekontun kabul edilen türleri (ADR 0061 §1; ortak ek kuyruğu). */
export const RECEIPT_ACCEPT = 'image/jpeg,image/png,application/pdf';

export interface SaveBody {
  periodId?: string;
  id?: string;
  rowVersion?: number;
  input: Record<string, unknown>;
}

interface FormProps {
  data: PayrollData;
  /** Yazılabilir mi (gemide yetki ve dönem durumu); değilse alanlar salt okunur. */
  editable: boolean;
  onSave: (kind: PayrollKind, body: SaveBody) => Promise<void>;
  onCancel: () => void;
  onRemove?: (() => void) | undefined;
}

/** Ortağın ayrıntı panelindeki "etiket — değer" satırı. */
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className={classes.field}>
      <span className={classes.fieldLabel}>{label}</span>
      <div className={classes.fieldValue}>{children}</div>
    </div>
  );
}

/**
 * Formun pencere içinde açıldığı bilgisi (ADR 0068 §6): ekran formu ortalanmış pencerede (Mantine
 * Modal) açar; başlık ve kapatma düğmesi pencerenindir, formun kendi başlığı çizilmez.
 */
export const PayrollModalContext = createContext(false);

/** Formun pencere başlığı (`payroll.form.*Title`). */
export const FORM_TITLE: Readonly<Record<PayrollKind, string>> = {
  lines: 'lineTitle',
  entries: 'entryTitle',
  extras: 'extraTitle',
  allotments: 'allotmentTitle',
  payments: 'paymentTitle',
};

function PanelShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const inModal = useContext(PayrollModalContext);
  if (inModal) {
    return (
      <section className={classes.modalPanel} aria-label={title}>
        {children}
      </section>
    );
  }
  return (
    <aside className={classes.panel} aria-label={title}>
      <div className={classes.panelHead}>
        <h2 className={classes.panelTitle}>{title}</h2>
        <ActionIcon
          variant="subtle"
          color="gray"
          aria-label={t('payroll.actions.cancel')}
          onClick={onClose}
        >
          <IconX size={18} />
        </ActionIcon>
      </div>
      {children}
    </aside>
  );
}

const currencyData = (current: string) =>
  [...new Set([current, ...PAYROLL_COMMON_CURRENCIES])].map((c) => ({ value: c, label: c }));

/** Kişi seçicisinin seçenekleri: ayın kadrosu ve dönemde satırı olanlar. */
function people(data: PayrollData): { value: string; label: string }[] {
  const map = new Map<string, string>();
  for (const p of data.roster) map.set(p.userId, p.rank ? `${p.name} — ${p.rank}` : p.name);
  for (const l of data.computation.lines) {
    if (!map.has(l.userId)) map.set(l.userId, l.rank ? `${l.name} — ${l.rank}` : l.name);
  }
  return [...map.entries()].map(([value, label]) => ({ value, label }));
}

function useSubmit(onDone: () => Promise<void>) {
  const message = usePayrollError();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      await onDone();
    } catch (failure) {
      setError(message(errorCode(failure)));
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, run };
}

function FormButtons({
  editable,
  busy,
  update,
  onCancel,
  error,
  extra,
}: {
  editable: boolean;
  busy: boolean;
  update: boolean;
  onCancel: () => void;
  error: string | null;
  extra?: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <>
      {error ? (
        <p className={classes.notice} data-tone="danger" role="alert">
          {error}
        </p>
      ) : null}
      {editable ? (
        <div className={classes.panelButtons}>
          <Button type="submit" loading={busy}>
            {t(update ? 'payroll.actions.update' : 'payroll.actions.save')}
          </Button>
          <Button variant="outline" color="red" onClick={onCancel}>
            {t('payroll.actions.cancel')}
          </Button>
        </div>
      ) : null}
      {extra}
    </>
  );
}

/* ---------- Bordro satırı ---------- */

export function LineForm({
  data,
  line,
  editable,
  onSave,
  onCancel,
  onRemove,
  onAddExtra,
  onOpenExtra,
}: FormProps & {
  line: PayrollLineResult | null;
  /** Kişiye extra ekle / extra kalemini aç (ADR 0068 §5). */
  onAddExtra?: (() => void) | undefined;
  onOpenExtra?: ((id: string) => void) | undefined;
}) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'en';
  const f = (key: string, options: Record<string, unknown> = {}) =>
    t(`payroll.form.${key}`, options);
  const dim = daysInPayrollMonth(data.month);
  const taken = new Set(data.computation.lines.map((l) => l.userId));
  const [userId, setUserId] = useState<string | null>(line?.userId ?? null);
  const roster = data.roster.find((p) => p.userId === userId);
  const [currency, setCurrency] = useState(line?.currency ?? DEFAULT_PAYROLL_CURRENCY);
  const [days, setDays] = useState<number | string>(
    line?.daysOnBoard ?? roster?.suggestedDays ?? dim,
  );
  const [wage, setWage] = useState<number | string>(fromMinor(line?.monthlyWage ?? null));
  const [deduct, setDeduct] = useState<number | string>(fromMinor(line?.otherDeductions ?? 0));
  const [deductNote, setDeductNote] = useState(line?.otherDeductionsNote ?? '');
  // Satırın tek "diğer kesinti" alanı yalnız eski kayıtta (ADR 0080 §4); yenisi kesinti girişi
  const legacyDeduction = (line?.otherDeductions ?? 0) > 0;
  const [remarks, setRemarks] = useState(line?.remarks ?? '');
  const [dirty, setDirty] = useState(false);
  useUnsavedChanges(dirty && editable);
  const touch =
    <T,>(set: (v: T) => void) =>
    (v: T) => {
      set(v);
      setDirty(true);
    };
  const ref = line ? data.lines.find((l) => l.id === line.id) : undefined;

  const calc = useMemo(() => {
    const basic = earnedBasic(toMinor(wage) ?? 0, Number(days) || 0, data.month);
    const own = data.entries.filter((e) => e.userId === userId);
    const sum = (kind: string) =>
      own.filter((e) => e.kind === kind).reduce((total, e) => total + e.amount, 0);
    const allotment = data.allotments
      .filter((a) => a.userId === userId && allotmentActive(a, data.month))
      .reduce((total, a) => total + allotmentAmount(a, basic), 0);
    // Extra: kişinin extra kalemleri ve eski tek alanlar (ADR 0068 §4)
    const earnings = basic + (line ? payrollExtraTotal(line) : 0);
    // Diğer kesintiler: satırın eski alanı + internet, telefon, şirket türü vb. (ADR 0080 §3)
    const other =
      (toMinor(deduct) ?? 0) +
      own
        .filter((e) => !['advance', 'slopchest'].includes(e.kind))
        .reduce((t, e) => t + e.amount, 0);
    const deductions = sum('advance') + sum('slopchest') + allotment + other;
    return {
      basic,
      earnings,
      advances: sum('advance'),
      slopchest: sum('slopchest'),
      allotment,
      other,
      deductions,
      net: earnings - deductions,
    };
  }, [wage, days, deduct, userId, data, line]);

  const submit = useSubmit(async () => {
    await onSave('lines', {
      ...(data.period ? { periodId: data.period.id } : {}),
      ...(line && ref ? { id: line.id, rowVersion: ref.rowVersion } : {}),
      input: {
        userId,
        currency,
        daysOnBoard: Number(days),
        monthlyWage: toMinor(wage) ?? 0,
        // Fazla mesai ve diğer kazanç extra kalemidir (ADR 0068); eski değer sunucuda taşınır
        otherDeductions: toMinor(deduct) ?? 0,
        otherDeductionsNote: deductNote || null,
        remarks: remarks || null,
      },
    });
    setDirty(false);
  });
  const money = (minor: number) => formatMoney(minor, language);
  const ro = !editable;

  return (
    <PanelShell title={f('lineTitle')} onClose={onCancel}>
      <form
        className={classes.panelForm}
        onSubmit={(event) => {
          event.preventDefault();
          void submit.run();
        }}
      >
        <Select
          label={f('crewMember')}
          placeholder={f('chooseCrew')}
          data={people(data).filter((p) => p.value === line?.userId || !taken.has(p.value))}
          value={userId}
          readOnly={ro || line !== null}
          searchable
          required
          onChange={(value) => {
            touch(setUserId)(value);
            const person = data.roster.find((p) => p.userId === value);
            if (person) setDays(person.suggestedDays);
          }}
        />
        <Select
          label={f('currency')}
          data={currencyData(currency)}
          value={currency}
          readOnly={ro}
          searchable
          allowDeselect={false}
          onChange={(value) => value && touch(setCurrency)(value)}
        />
        <NumberInput
          label={f('days')}
          description={
            roster && !ro && Number(days) !== roster.suggestedDays ? (
              <Anchor
                component="button"
                type="button"
                size="xs"
                onClick={() => touch(setDays)(roster.suggestedDays)}
              >
                {t('payroll.actions.useSuggested', { days: roster.suggestedDays })}
              </Anchor>
            ) : (
              f('daysHint', { days: dim })
            )
          }
          min={0}
          max={dim}
          allowDecimal={false}
          readOnly={ro}
          value={days}
          onChange={touch(setDays)}
        />
        <NumberInput
          label={f('monthlyWage')}
          min={0}
          decimalScale={2}
          fixedDecimalScale
          thousandSeparator=","
          readOnly={ro}
          required
          value={wage}
          onChange={touch(setWage)}
        />
        <ExtrasBlock
          line={line}
          editable={editable && line !== null}
          onAdd={onAddExtra}
          onOpen={onOpenExtra}
        />
        <DeductionsBlock line={line} />
        {legacyDeduction ? (
          <NumberInput
            label={f('otherDeductions')}
            description={f('legacyDeduction')}
            min={0}
            decimalScale={2}
            fixedDecimalScale
            thousandSeparator=","
            readOnly={ro}
            value={deduct}
            onChange={touch(setDeduct)}
          />
        ) : null}
        {legacyDeduction && (toMinor(deduct) ?? 0) > 0 ? (
          <TextInput
            label={f('otherDeductionsNote')}
            required
            maxLength={200}
            readOnly={ro}
            value={deductNote}
            onChange={(e) => touch(setDeductNote)(e.currentTarget.value)}
          />
        ) : null}
        <Textarea
          label={f('remarks')}
          maxLength={500}
          autosize
          minRows={1}
          readOnly={ro}
          value={remarks}
          onChange={(e) => touch(setRemarks)(e.currentTarget.value)}
        />
        <dl className={classes.calc} aria-label={f('summary')}>
          <dt>{f('basicEarned')}</dt>
          <dd>{money(calc.basic)}</dd>
          <dt>{t('payroll.columns.earnings')}</dt>
          <dd>{money(calc.earnings)}</dd>
          {/* Ortağın sırası (ADR 0066 §6): Home Allotment, Cash Advance, Slopchest */}
          <dt>{t('payroll.columns.allotment')}</dt>
          <dd>−{money(calc.allotment)}</dd>
          <dt>{t('payroll.columns.advances')}</dt>
          <dd>−{money(calc.advances)}</dd>
          <dt>{t('payroll.columns.slopchest')}</dt>
          <dd>−{money(calc.slopchest)}</dd>
          {calc.other > 0 ? (
            <>
              <dt>{t('payroll.columns.otherDeductions')}</dt>
              <dd>−{money(calc.other)}</dd>
            </>
          ) : null}
          <dt className={classes.calcTotal}>{f('net')}</dt>
          <dd className={`${classes.calcTotal} ${calc.net < 0 ? classes.negative : ''}`}>
            {currency} {money(calc.net)}
          </dd>
        </dl>
        <FormButtons
          editable={editable}
          busy={submit.busy}
          update={line !== null}
          onCancel={onCancel}
          error={submit.error}
          extra={
            editable && line && onRemove ? (
              <Button variant="subtle" color="red" onClick={onRemove}>
                {t('payroll.actions.remove')}
              </Button>
            ) : null
          }
        />
      </form>
    </PanelShell>
  );
}

/** Satır formunda kişinin extra dökümü (ADR 0068 §5): kalemler, toplam ve ekleme. */
function ExtrasBlock({
  line,
  editable,
  onAdd,
  onOpen,
}: {
  line: PayrollLineResult | null;
  editable: boolean;
  onAdd?: (() => void) | undefined;
  onOpen?: ((id: string) => void) | undefined;
}) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'en';
  const items = line ? payrollExtraItems(line) : [];
  const tt = (key: string) => t(key);
  return (
    <section className={classes.extras} aria-label={t('payroll.form.extras')}>
      <div className={classes.extrasHead}>
        <span className={classes.fieldLabel}>{t('payroll.form.extras')}</span>
        {editable && onAdd ? (
          <Button
            size="compact-sm"
            variant="light"
            leftSection={<IconPlus size={14} />}
            onClick={onAdd}
          >
            {t('payroll.actions.addExtra')}
          </Button>
        ) : null}
      </div>
      {items.length === 0 ? (
        <p className={classes.sub}>{t('payroll.form.noExtras')}</p>
      ) : (
        <ul className={classes.extraList}>
          {items.map((item) => (
            <li key={item.id}>
              {!item.legacy && onOpen ? (
                <button type="button" className={classes.rowButton} onClick={() => onOpen(item.id)}>
                  {extraLabel(item, tt, language)}
                </button>
              ) : (
                <span title={item.legacy ? t('payroll.form.legacyExtra') : undefined}>
                  {extraLabel(item, tt, language)}
                </span>
              )}
              <span className={classes.numeric}>{formatMoney(item.amount, language)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * Satır formunda kişinin diğer kesinti dökümü (ADR 0080 §5): internet, telefon, şirket türü vb.
 * kesintiler Advances & Deductions'tan girilir; burada yalnız görünür. Kesinti yoksa çizilmez.
 */
function DeductionsBlock({ line }: { line: PayrollLineResult | null }) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'en';
  const items = line ? payrollDeductionItems(line).filter((x) => !x.legacy) : [];
  if (items.length === 0) return null;
  const tt = (key: string) => t(key);
  return (
    <section className={classes.extras} aria-label={t('payroll.form.deductions')}>
      <div className={classes.extrasHead}>
        <span className={classes.fieldLabel}>{t('payroll.form.deductions')}</span>
      </div>
      <ul className={classes.extraList}>
        {items.map((item) => (
          <li key={item.id}>
            <span>{deductionLabel(item, tt, language)}</span>
            <span className={classes.numeric}>{formatMoney(item.amount, language)}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Tür seçicisinin değeri: hazır tür kodu ya da `company:<kimlik>`. */
const typeValue = (type: PayrollExtraType, typeId: string | null) =>
  type === 'company' && typeId ? `company:${typeId}` : type;

/**
 * Extra kalemi (ADR 0068 §1, §2): kişi (dönemde satırı olan), tür (hazır türler ve şirket türleri),
 * açıklama ("Other"da zorunlu), isteğe bağlı miktar × birim ücret, tutar; para birimi kişinin.
 */
export function ExtraForm({
  data,
  extraId,
  userId: presetUser,
  editable,
  onSave,
  onCancel,
  onRemove,
}: FormProps & { extraId: string | null; userId?: string | null | undefined }) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'en';
  const f = (key: string) => t(`payroll.form.${key}`);
  const extra = (data.extras ?? []).find((x) => x.id === extraId) ?? null;
  const [userId, setUserId] = useState<string | null>(extra?.userId ?? presetUser ?? null);
  const [type, setType] = useState<string>(extra ? typeValue(extra.type, extra.typeId) : 'bonus');
  const [description, setDescription] = useState(extra?.description ?? '');
  const [quantity, setQuantity] = useState<number | string>(extra?.quantity ?? '');
  const [rate, setRate] = useState<number | string>(fromMinor(extra?.rate ?? null));
  const [amount, setAmount] = useState<number | string>(fromMinor(extra?.amount ?? null));
  const [dirty, setDirty] = useState(false);
  useUnsavedChanges(dirty && editable);
  const touch =
    <T,>(set: (v: T) => void) =>
    (v: T) => {
      set(v);
      setDirty(true);
    };
  const line = data.computation.lines.find((l) => l.userId === userId);
  const currency = extra?.currency ?? line?.currency ?? DEFAULT_PAYROLL_CURRENCY;
  const company = data.extraTypes ?? [];
  const typeData = [
    {
      group: t('payroll.extraGroups.common'),
      items: PAYROLL_EXTRA_TYPES.map((code) => ({
        value: code,
        label: extraTypeLabel({ type: code, typeName: null }, (key) => t(key)),
      })),
    },
    ...(company.length > 0 || extra?.type === 'company'
      ? [
          {
            group: t('payroll.extraGroups.company'),
            items: [
              ...company.map((c) => ({ value: `company:${c.id}`, label: c.name })),
              // Kaldırılmış şirket türü eski kalemde adıyla görünür
              ...(extra?.type === 'company' &&
              extra.typeId &&
              !company.some((c) => c.id === extra.typeId)
                ? [{ value: `company:${extra.typeId}`, label: extra.typeName ?? extra.typeId }]
                : []),
            ],
          },
        ]
      : []),
  ];
  const submit = useSubmit(async () => {
    const [code, typeId] = type.startsWith('company:')
      ? ['company', type.slice('company:'.length)]
      : [type, null];
    await onSave('extras', {
      ...(data.period ? { periodId: data.period.id } : {}),
      ...(extra ? { id: extra.id, rowVersion: extra.rowVersion } : {}),
      input: {
        userId,
        type: code,
        typeId,
        description: description || null,
        quantity: quantity === '' ? null : Number(quantity),
        rate: toMinor(rate),
        amount: toMinor(amount),
        currency,
      },
    });
    setDirty(false);
  });
  const ro = !editable;
  const withLines = data.computation.lines.map((l) => ({
    value: l.userId,
    label: l.rank ? `${l.name} — ${l.rank}` : l.name,
  }));
  return (
    <PanelShell title={f('extraTitle')} onClose={onCancel}>
      <form
        className={classes.panelForm}
        onSubmit={(event) => {
          event.preventDefault();
          void submit.run();
        }}
      >
        <Select
          label={f('crewMember')}
          placeholder={f('chooseCrew')}
          data={withLines}
          value={userId}
          readOnly={ro || extra !== null}
          searchable
          required
          onChange={touch(setUserId)}
        />
        <Select
          label={f('extraType')}
          data={typeData}
          value={type}
          readOnly={ro}
          searchable
          allowDeselect={false}
          required
          onChange={(value) => value && touch(setType)(value)}
        />
        <TextInput
          label={f('description')}
          maxLength={200}
          required={type === 'other'}
          readOnly={ro}
          value={description}
          onChange={(e) => touch(setDescription)(e.currentTarget.value)}
        />
        <div className={classes.formRow}>
          <NumberInput
            label={f('quantity')}
            min={0}
            max={9999}
            decimalScale={1}
            readOnly={ro}
            value={quantity}
            onChange={touch(setQuantity)}
          />
          <NumberInput
            label={f('rate')}
            min={0}
            decimalScale={2}
            fixedDecimalScale
            thousandSeparator=","
            readOnly={ro}
            value={rate}
            onChange={touch(setRate)}
          />
        </div>
        <NumberInput
          label={`${f('amount')} (${currency})`}
          description={f('amountHint')}
          required
          min={0}
          decimalScale={2}
          fixedDecimalScale
          thousandSeparator=","
          readOnly={ro}
          value={amount}
          onChange={touch(setAmount)}
          rightSectionWidth={quantity !== '' && rate !== '' && !ro ? 104 : undefined}
          rightSection={
            quantity !== '' && rate !== '' && !ro ? (
              <Button
                size="compact-xs"
                variant="light"
                onClick={() =>
                  touch(setAmount)(
                    fromMinor(overtimeAmountOf(Number(quantity), toMinor(rate) ?? 0)),
                  )
                }
              >
                {t('payroll.actions.computeAmount')}
              </Button>
            ) : undefined
          }
        />
        {line ? (
          <p className={classes.sub}>
            {f('extraTotal')}: {currency} {formatMoney(payrollExtraTotal(line), language)}
          </p>
        ) : null}
        <FormButtons
          editable={editable}
          busy={submit.busy}
          update={extra !== null}
          onCancel={onCancel}
          error={submit.error}
          extra={
            editable && extra && onRemove ? (
              <Button variant="subtle" color="red" onClick={onRemove}>
                {t('payroll.actions.remove')}
              </Button>
            ) : null
          }
        />
      </form>
    </PanelShell>
  );
}

/* ---------- Avans, slopchest ve kesinti ---------- */

/** Giriş türü seçicisinin değeri: tür kodu ya da `company:<kimlik>` (ADR 0080 §2). */
const kindValue = (kind: PayrollEntryKind, typeId: string | null | undefined) =>
  kind === 'company' && typeId ? `company:${typeId}` : kind;

/**
 * Advances & Deductions girişi (ADR 0061 §2; ADR 0080 §1): avans ve slopchest kendi kaleminde;
 * internet, telefon vb. hazır kesinti türleri ve şirketin etkin kesinti türleri "Other
 * Deductions" kaleminde. Pasiflenen tür yalnız eski kayıtta görünür.
 */
export function EntryForm({
  data,
  entryId,
  editable,
  onSave,
  onCancel,
  onRemove,
}: FormProps & { entryId: string | null }) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'en';
  const f = (key: string) => t(`payroll.form.${key}`);
  const entry = data.entries.find((e) => e.id === entryId) ?? null;
  const lineCurrency = (user: string | null) =>
    data.computation.lines.find((l) => l.userId === user)?.currency ?? DEFAULT_PAYROLL_CURRENCY;
  const [userId, setUserId] = useState<string | null>(entry?.userId ?? null);
  const [kind, setKind] = useState<string>(entry ? kindValue(entry.kind, entry.typeId) : 'advance');
  const [day, setDay] = useState<string | null>(entry?.entryDay ?? null);
  const [amount, setAmount] = useState<number | string>(fromMinor(entry?.amount ?? null));
  const [currency, setCurrency] = useState(entry?.currency ?? DEFAULT_PAYROLL_CURRENCY);
  const [description, setDescription] = useState(entry?.description ?? '');
  const [dirty, setDirty] = useState(false);
  useUnsavedChanges(dirty && editable);
  const first = `${data.month}-01`;
  const last = `${data.month}-${String(daysInPayrollMonth(data.month)).padStart(2, '0')}`;
  const submit = useSubmit(async () => {
    await onSave('entries', {
      ...(data.period ? { periodId: data.period.id } : {}),
      ...(entry ? { id: entry.id, rowVersion: entry.rowVersion } : {}),
      input: {
        userId,
        ...(kind.startsWith('company:')
          ? { kind: 'company', typeId: kind.slice('company:'.length) }
          : { kind, typeId: null }),
        entryDay: day,
        amount: toMinor(amount),
        currency,
        description: description || null,
      },
    });
    setDirty(false);
  });
  const ro = !editable;
  const label = (code: PayrollEntryKind) =>
    deductionTypeLabel({ type: code }, (key) => t(key), language);
  const company = (data.deductionTypes ?? []).filter((c) => c.active);
  // Pasiflenen şirket türü eski kayıtta adıyla görünür (ADR 0080 §2)
  const retired =
    entry?.kind === 'company' && entry.typeId && !company.some((c) => c.id === entry.typeId)
      ? [
          {
            value: `company:${entry.typeId}`,
            label: deductionTypeLabel(
              { type: entry.kind, typeName: entry.typeName, typeNameTr: entry.typeNameTr },
              (key) => t(key),
              language,
            ),
          },
        ]
      : [];
  const kindData = [
    {
      group: t('payroll.kindGroups.entries'),
      items: PAYROLL_ENTRY_KINDS.map((code) => ({ value: code, label: label(code) })),
    },
    {
      group: t('payroll.kindGroups.common'),
      items: PAYROLL_DEDUCTION_TYPES.map((code) => ({ value: code, label: label(code) })),
    },
    ...(company.length > 0 || retired.length > 0
      ? [
          {
            group: t('payroll.kindGroups.company'),
            items: [
              ...company.map((c) => ({
                value: `company:${c.id}`,
                label: deductionTypeLabel(
                  { type: 'company', typeName: c.name, typeNameTr: c.nameTr },
                  (key) => t(key),
                  language,
                ),
              })),
              ...retired,
            ],
          },
        ]
      : []),
  ];
  const deduction = !(PAYROLL_ENTRY_KINDS as readonly string[]).includes(kind);
  const described = (PAYROLL_DEDUCTION_DESCRIBED as readonly string[]).includes(kind);
  return (
    <PanelShell title={f('entryTitle')} onClose={onCancel}>
      <form
        className={classes.panelForm}
        onSubmit={(event) => {
          event.preventDefault();
          void submit.run();
        }}
      >
        <Select
          label={f('crewMember')}
          placeholder={f('chooseCrew')}
          data={people(data)}
          value={userId}
          readOnly={ro || entry !== null}
          searchable
          required
          onChange={(value) => {
            setUserId(value);
            setCurrency(lineCurrency(value));
            setDirty(true);
          }}
        />
        <Select
          label={f('kind')}
          data={kindData}
          value={kind}
          allowDeselect={false}
          searchable
          readOnly={ro}
          description={deduction && !ro ? f('deductionHint') : undefined}
          onChange={(value) => {
            if (value) setKind(value);
            setDirty(true);
          }}
        />
        <DayInput
          label={f('day')}
          required
          readOnly={ro}
          value={day}
          minDate={first}
          maxDate={last}
          onChange={(value) => {
            setDay(value);
            setDirty(true);
          }}
        />
        <NumberInput
          label={f('amount')}
          required
          min={0}
          decimalScale={2}
          fixedDecimalScale
          thousandSeparator=","
          readOnly={ro}
          value={amount}
          onChange={(value) => {
            setAmount(value);
            setDirty(true);
          }}
        />
        <Select
          label={f('currency')}
          data={currencyData(currency)}
          value={currency}
          searchable
          allowDeselect={false}
          readOnly={ro}
          onChange={(value) => {
            if (value) setCurrency(value);
            setDirty(true);
          }}
        />
        <TextInput
          label={f('description')}
          maxLength={200}
          required={described}
          readOnly={ro}
          value={description}
          onChange={(e) => {
            setDescription(e.currentTarget.value);
            setDirty(true);
          }}
        />
        <FormButtons
          editable={editable}
          busy={submit.busy}
          update={entry !== null}
          onCancel={onCancel}
          error={submit.error}
          extra={
            editable && entry && onRemove ? (
              <Button variant="subtle" color="red" onClick={onRemove}>
                {t('payroll.actions.remove')}
              </Button>
            ) : null
          }
        />
      </form>
    </PanelShell>
  );
}

/* ---------- Allotment ---------- */

/** Allotment ayı seçenekleri: iki yıl öncesinden bir yıl sonrasına. */
function monthChoices(around: string, language: string): { value: string; label: string }[] {
  const [year, mon] = around.split('-').map(Number);
  const out: { value: string; label: string }[] = [];
  for (let i = -24; i <= 12; i += 1) {
    const date = new Date(Date.UTC(year ?? 2026, (mon ?? 1) - 1 + i, 1));
    const value = date.toISOString().slice(0, 7);
    out.push({ value, label: monthLabel(value, language) });
  }
  return out.reverse();
}

export function AllotmentForm({
  data,
  allotmentId,
  editable,
  onSave,
  onCancel,
  onRemove,
}: FormProps & { allotmentId: string | null }) {
  const { t, i18n } = useTranslation();
  const f = (key: string) => t(`payroll.form.${key}`);
  const language = i18n.resolvedLanguage ?? 'en';
  const item = data.allotments.find((a) => a.id === allotmentId) ?? null;
  const [userId, setUserId] = useState<string | null>(item?.userId ?? null);
  const [beneficiary, setBeneficiary] = useState(item?.beneficiaryName ?? '');
  const [relationship, setRelationship] = useState(item?.relationship ?? '');
  const [bank, setBank] = useState(item?.bankDetails ?? '');
  const [basis, setBasis] = useState<PayrollAllotmentBasis>(item?.basis ?? 'fixed');
  const [amount, setAmount] = useState<number | string>(fromMinor(item?.amount ?? null));
  const [percent, setPercent] = useState<number | string>(
    item?.percentBp === null || item?.percentBp === undefined ? '' : item.percentBp / 100,
  );
  const [currency, setCurrency] = useState(item?.currency ?? DEFAULT_PAYROLL_CURRENCY);
  const [start, setStart] = useState<string | null>(item?.startMonth ?? data.month);
  const [end, setEnd] = useState<string | null>(item?.endMonth ?? null);
  const [remarks, setRemarks] = useState(item?.remarks ?? '');
  const [dirty, setDirty] = useState(false);
  useUnsavedChanges(dirty && editable);
  const months = useMemo(() => monthChoices(data.month, language), [data.month, language]);
  const submit = useSubmit(async () => {
    await onSave('allotments', {
      ...(item ? { id: item.id, rowVersion: item.rowVersion } : {}),
      input: {
        userId,
        beneficiaryName: beneficiary,
        relationship: relationship || null,
        bankDetails: bank || null,
        basis,
        amount: basis === 'fixed' ? toMinor(amount) : null,
        percentBp: basis === 'percent' ? toMinor(percent) : null,
        currency,
        startMonth: start,
        endMonth: end,
        remarks: remarks || null,
      },
    });
    setDirty(false);
  });
  const ro = !editable;
  const change =
    <T,>(set: (v: T) => void) =>
    (v: T) => {
      set(v);
      setDirty(true);
    };
  return (
    <PanelShell title={f('allotmentTitle')} onClose={onCancel}>
      <form
        className={classes.panelForm}
        onSubmit={(event) => {
          event.preventDefault();
          void submit.run();
        }}
      >
        <Select
          label={f('crewMember')}
          placeholder={f('chooseCrew')}
          data={people(data)}
          value={userId}
          readOnly={ro || item !== null}
          searchable
          required
          onChange={(value) => {
            change(setUserId)(value);
            const line = data.computation.lines.find((l) => l.userId === value);
            if (line) setCurrency(line.currency);
          }}
        />
        <TextInput
          label={f('beneficiary')}
          required
          maxLength={200}
          readOnly={ro}
          value={beneficiary}
          onChange={(e) => change(setBeneficiary)(e.currentTarget.value)}
        />
        <TextInput
          label={f('relationship')}
          maxLength={100}
          readOnly={ro}
          value={relationship}
          onChange={(e) => change(setRelationship)(e.currentTarget.value)}
        />
        <Textarea
          label={f('bankDetails')}
          maxLength={500}
          autosize
          minRows={2}
          readOnly={ro}
          value={bank}
          onChange={(e) => change(setBank)(e.currentTarget.value)}
        />
        <Select
          label={f('basis')}
          data={PAYROLL_ALLOTMENT_BASES.map((b) => ({ value: b, label: t(`payroll.bases.${b}`) }))}
          value={basis}
          allowDeselect={false}
          readOnly={ro}
          onChange={(value) => value && change(setBasis)(value as PayrollAllotmentBasis)}
        />
        {basis === 'fixed' ? (
          <NumberInput
            label={f('amount')}
            required
            min={0}
            decimalScale={2}
            fixedDecimalScale
            thousandSeparator=","
            readOnly={ro}
            value={amount}
            onChange={change(setAmount)}
          />
        ) : (
          <NumberInput
            label={f('percent')}
            required
            min={0.01}
            max={100}
            decimalScale={2}
            suffix=" %"
            readOnly={ro}
            value={percent}
            onChange={change(setPercent)}
          />
        )}
        <Select
          label={f('currency')}
          data={currencyData(currency)}
          value={currency}
          searchable
          allowDeselect={false}
          readOnly={ro}
          onChange={(value) => value && change(setCurrency)(value)}
        />
        <Select
          label={f('startMonth')}
          data={months}
          value={start}
          required
          allowDeselect={false}
          readOnly={ro}
          onChange={change(setStart)}
        />
        <Select
          label={f('endMonth')}
          data={months}
          value={end}
          clearable
          readOnly={ro}
          onChange={change(setEnd)}
        />
        <Textarea
          label={f('remarks')}
          maxLength={500}
          autosize
          minRows={1}
          readOnly={ro}
          value={remarks}
          onChange={(e) => change(setRemarks)(e.currentTarget.value)}
        />
        <FormButtons
          editable={editable}
          busy={submit.busy}
          update={item !== null}
          onCancel={onCancel}
          error={submit.error}
          extra={
            editable && item && onRemove ? (
              <Button variant="subtle" color="red" onClick={onRemove}>
                {t('payroll.actions.remove')}
              </Button>
            ) : null
          }
        />
      </form>
    </PanelShell>
  );
}

/* ---------- Ödeme ayrıntısı ---------- */

export function PaymentStatusBadge({ status }: { status: PayrollPayment['status'] }) {
  const { t } = useTranslation();
  return (
    <StatusBadge tone={status === 'reconciled' ? 'info' : 'success'} variant="pill">
      {`● ${t(`payroll.paymentStatus.${status}`)}`}
    </StatusBadge>
  );
}

export function PaymentForm({
  data,
  paymentId,
  editable,
  onSave,
  onCancel,
  onRemove,
  onReconcile,
  documents,
  documentUrl,
  onUpload,
}: FormProps & {
  paymentId: string | null;
  onReconcile?: (() => void) | undefined;
  documents: PayrollDocument[] | null;
  /** Dekontun açılma adresi (ofiste); yoksa kart yalnız adı gösterir. */
  documentUrl?: ((doc: PayrollDocument) => string | undefined) | undefined;
  onUpload?: ((file: File) => Promise<void>) | undefined;
}) {
  const { t } = useTranslation();
  const f = (key: string) => t(`payroll.form.${key}`);
  const message = usePayrollError();
  const payment = data.payments.find((p) => p.id === paymentId) ?? null;
  const reconciled = payment?.status === 'reconciled';
  const ro = !editable || reconciled;
  const lineOf = (user: string | null) => data.computation.lines.find((l) => l.userId === user);
  const [userId, setUserId] = useState<string | null>(payment?.userId ?? null);
  const [type, setType] = useState<PayrollPaymentType>(payment?.paymentType ?? 'salary');
  const [allotmentId, setAllotmentId] = useState<string | null>(payment?.allotmentId ?? null);
  const [amount, setAmount] = useState<number | string>(fromMinor(payment?.amount ?? null));
  const [currency, setCurrency] = useState(payment?.currency ?? DEFAULT_PAYROLL_CURRENCY);
  const [day, setDay] = useState<string | null>(payment?.paymentDay ?? null);
  const [method, setMethod] = useState<PayrollPaymentMethod>(payment?.method ?? 'bank_transfer');
  const [reference, setReference] = useState(payment?.reference ?? '');
  const [bankAccount, setBankAccount] = useState(payment?.bankAccount ?? '');
  const [remarks, setRemarks] = useState(payment?.remarks ?? '');
  const [dirty, setDirty] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  useUnsavedChanges(dirty && !ro);
  const change =
    <T,>(set: (v: T) => void) =>
    (v: T) => {
      set(v);
      setDirty(true);
    };
  const allotments = data.allotments.filter((a) => a.userId === userId);
  const submit = useSubmit(async () => {
    await onSave('payments', {
      ...(data.period ? { periodId: data.period.id } : {}),
      ...(payment ? { id: payment.id, rowVersion: payment.rowVersion } : {}),
      input: {
        userId,
        paymentType: type,
        allotmentId: type === 'allotment' ? allotmentId : null,
        amount: toMinor(amount),
        currency,
        paymentDay: day,
        method,
        reference: reference || null,
        bankAccount: bankAccount || null,
        remarks: remarks || null,
      },
    });
    setDirty(false);
  });
  return (
    <PanelShell title={f('paymentTitle')} onClose={onCancel}>
      <form
        className={classes.panelForm}
        onSubmit={(event) => {
          event.preventDefault();
          void submit.run();
        }}
      >
        {payment ? (
          <div>
            <span className={classes.fieldLabel}>{f('crewMember')}</span>
            <span className={`${classes.fieldValue} ${classes.block}`}>{payment.name}</span>
            {payment.rank ? <span className={classes.sub}>{payment.rank}</span> : null}
          </div>
        ) : (
          <Select
            label={f('crewMember')}
            placeholder={f('chooseCrew')}
            data={people(data)}
            value={userId}
            searchable
            required
            readOnly={ro}
            onChange={(value) => {
              change(setUserId)(value);
              const line = lineOf(value);
              if (line) {
                setCurrency(line.currency);
                setAmount(fromMinor(line.net));
              }
            }}
          />
        )}
        <Field label={f('paymentType')}>
          <Select
            aria-label={f('paymentType')}
            data={PAYROLL_PAYMENT_TYPES.map((v) => ({
              value: v,
              label: t(`payroll.paymentTypes.${v}`),
            }))}
            value={type}
            allowDeselect={false}
            readOnly={ro}
            onChange={(value) => value && change(setType)(value as PayrollPaymentType)}
          />
        </Field>
        {type === 'allotment' ? (
          <Field label={f('allotment')}>
            <Select
              aria-label={f('allotment')}
              data={allotments.map((a) => ({ value: a.id, label: a.beneficiaryName }))}
              value={allotmentId}
              clearable
              readOnly={ro}
              onChange={change(setAllotmentId)}
            />
          </Field>
        ) : null}
        <Field label={`${f('amount')} (${currency})`}>
          <NumberInput
            aria-label={f('amount')}
            required
            min={0}
            decimalScale={2}
            fixedDecimalScale
            thousandSeparator=","
            readOnly={ro}
            value={amount}
            onChange={change(setAmount)}
          />
        </Field>
        <Field label={f('currency')}>
          <Select
            aria-label={f('currency')}
            data={currencyData(currency)}
            value={currency}
            searchable
            allowDeselect={false}
            readOnly={ro}
            onChange={(value) => value && change(setCurrency)(value)}
          />
        </Field>
        <Field label={f('paymentDay')}>
          <DayInput
            aria-label={f('paymentDay')}
            required
            readOnly={ro}
            value={day}
            onChange={change(setDay)}
          />
        </Field>
        <Field label={f('method')}>
          <Select
            aria-label={f('method')}
            data={PAYROLL_PAYMENT_METHODS.map((m) => ({
              value: m,
              label: t(`payroll.methods.${m}`),
            }))}
            value={method}
            allowDeselect={false}
            readOnly={ro}
            onChange={(value) => value && change(setMethod)(value as PayrollPaymentMethod)}
          />
        </Field>
        <Field label={f('reference')}>
          <TextInput
            aria-label={f('reference')}
            maxLength={100}
            required={method === 'bank_transfer'}
            readOnly={ro}
            value={reference}
            onChange={(e) => change(setReference)(e.currentTarget.value)}
          />
        </Field>
        {payment ? (
          <Field label={f('status')}>
            <PaymentStatusBadge status={payment.status} />
          </Field>
        ) : null}
        {payment ? (
          <Field label={f('recordedBy')}>
            <span className={classes.fieldValue}>
              {[payment.recordedByName, t(`payroll.recordedOn.${payment.recordedOn}`)]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </Field>
        ) : null}
        <Field label={f('bankAccount')}>
          <TextInput
            aria-label={f('bankAccount')}
            maxLength={200}
            readOnly={ro}
            value={bankAccount}
            onChange={(e) => change(setBankAccount)(e.currentTarget.value)}
          />
        </Field>
        <Textarea
          label={f('remarks')}
          maxLength={500}
          autosize
          minRows={2}
          readOnly={ro}
          value={remarks}
          onChange={(e) => change(setRemarks)(e.currentTarget.value)}
        />
        {payment ? (
          <div>
            <div className={classes.panelHead}>
              <span className={classes.label}>{f('attachments')}</span>
              {onUpload ? (
                <FileButton
                  accept={RECEIPT_ACCEPT}
                  onChange={async (file) => {
                    if (!file) return;
                    setUploading(true);
                    setUploadError(null);
                    try {
                      await onUpload(file);
                    } catch (failure) {
                      setUploadError(message(errorCode(failure)));
                    } finally {
                      setUploading(false);
                    }
                  }}
                >
                  {(props) => (
                    <Button
                      {...props}
                      size="xs"
                      variant="light"
                      loading={uploading}
                      leftSection={<IconUpload size={14} />}
                    >
                      {t('payroll.actions.upload')}
                    </Button>
                  )}
                </FileButton>
              ) : null}
            </div>
            {documents === null ? null : documents.length === 0 ? (
              <p className={classes.note}>{f('noAttachments')}</p>
            ) : (
              <ul className={classes.documents}>
                {documents.map((d) => (
                  <AttachmentCard
                    key={d.id}
                    attachment={d}
                    // Gemide yalnız geminin eki açılır; ofisin dekontu bulut deposundadır
                    href={
                      documentUrl ? documentUrl(d) : d.source === 'attachment' ? undefined : null
                    }
                  />
                ))}
              </ul>
            )}
            {onUpload ? <p className={classes.note}>{f('attachmentTypes')}</p> : null}
            {uploadError ? (
              <p className={classes.notice} data-tone="danger" role="alert">
                {uploadError}
              </p>
            ) : null}
          </div>
        ) : null}
        {reconciled ? <p className={classes.note}>{f('reconciledNote')}</p> : null}
        <FormButtons
          editable={!ro}
          busy={submit.busy}
          update={payment !== null}
          onCancel={onCancel}
          error={submit.error}
          extra={
            !ro && payment ? (
              <div className={classes.panelButtons}>
                {onReconcile ? (
                  <Button variant="light" onClick={onReconcile}>
                    {t('payroll.actions.reconcile')}
                  </Button>
                ) : (
                  <span />
                )}
                {onRemove ? (
                  <Button variant="subtle" color="red" onClick={onRemove}>
                    {t('payroll.actions.remove')}
                  </Button>
                ) : null}
              </div>
            ) : null
          }
        />
      </form>
    </PanelShell>
  );
}
